import React, { useEffect, useState } from "react";
import { Zap, TrendingUp, Clock, Activity, Droplets } from "lucide-react";

/**
 * variant="gauge"  — Speedometer arc
 * variant="wave"   — Blood-glucose spike curve
 * variant="ring"   — Circular donut ring + big number
 * variant="hero"   — Full rich-gradient hero card (recommended ★)
 * variant="curve"  — Glucose Response Curve with time markers (0-90min)
 */

type GlucoseCurvePoint = {
  time_min: number;
  glucose_mg_dl: number;
  new_meal_rise: number;
  prev_meal_remaining: number;
  total_rise: number;
  phase: string;
};

/** Unified spike palette (low → moderate → high) across arc, wave, curve, and zone bands */
const GLUCOSE_CHART = {
  low: "#10b981",
  moderate: "#f97316",
  high: "#ef4444",
} as const;

type GlucoseCurve = {
  points: GlucoseCurvePoint[];
  is_cumulative: boolean;
  starting_glucose: number;
  peak_glucose: number;
  peak_time_min: number;
  recovery_time_min: number;
};

interface GlucoseSpikeData {
  spike_category: string;
  spike_band: string;
  spike_range: {
    low: number;
    high: number;
  };
  spike_level: number;
  color: string;
  emoji: string;
  title: string;
  description: string;
  ui_message: string;
  predicted_spike_mg_dl: number;
  this_meal_spike_mg_dl: number;
  is_cumulative: boolean;
  previous_meal_remaining_mg_dl: number;
  previous_meal_elapsed_min: number | null;
  cumulative_note: string | null;
  confidence: string;
  confidence_margin_mg_dl: number;
  time_to_peak_min: number;
  recovery_time_min: number;
  recommendations: string[];
  baseline_glucose?: number;
  predicted_peak_glucose?: number;
  predicted_peak_glucose_this_meal_only?: number;
  glucose_curve?: GlucoseCurve;
}

interface PredictedGlucoseSpikeCardProps {
  glucoseSpikeData?: GlucoseSpikeData;
  // Fallback props for pages without API glucose_spike data
  spikeValue?: number;
  mealsLogged?: number;
  variant?: "gauge" | "wave" | "ring" | "hero" | "curve";
  showPredictedRange?: boolean; // Controls visibility of Predicted Range section
}

// Gauge geometry constants
const CX = 65;
const CY = 66;
const R = 52;
const ARC_LEN = Math.PI * R; // half-circle arc length ≈ 163.4

const PredictedGlucoseSpikeCard: React.FC<PredictedGlucoseSpikeCardProps> = ({
  glucoseSpikeData,
  spikeValue,
  mealsLogged = 0,
  variant = "gauge",
  showPredictedRange = true,
}) => {
  // Determine spike value - prefer this_meal_spike, fallback to predicted, then prop
  const spike = glucoseSpikeData?.this_meal_spike_mg_dl ?? glucoseSpikeData?.predicted_spike_mg_dl ?? spikeValue ?? 0;
  
  // Early return if no data
  if (spike === 0 && mealsLogged === 0) {
    return null;
  }

  const MAX_SPIKE = 160;
  const spikePercent = Math.min(spike / MAX_SPIKE, 1);

  // Time values - from API or calculated fallback
  const timeToPeak = glucoseSpikeData?.time_to_peak_min ?? (spike < 40 ? 45 : spike < 80 ? 35 : 25);
  const recoveryTime = glucoseSpikeData?.recovery_time_min ?? (spike < 40 ? 120 : spike < 80 ? 90 : 60);

  // Confidence - from API or calculated fallback
  const confidence = (() => {
    if (glucoseSpikeData?.confidence) {
      const conf = glucoseSpikeData.confidence.toLowerCase();
      if (conf === "high") return { label: "High", color: "text-green-600 dark:text-green-400" };
      if (conf === "medium" || conf === "moderate") return { label: "Medium", color: "text-amber-600 dark:text-amber-400" };
      return { label: "Low", color: "text-orange-600 dark:text-orange-400" };
    }
    // Fallback calculation
    if (mealsLogged === 0) return { label: "Low", color: "text-red-500" };
    if (mealsLogged <= 2) return { label: "Moderate", color: "text-amber-500" };
    return { label: "High", color: "text-green-600" };
  })();

  // Animate from 0 → actual value after mount
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    if (spike === 0 && mealsLogged === 0) return;
    const t = setTimeout(() => setAnimPct(spikePercent), 150);
    return () => clearTimeout(t);
  }, [spikePercent, mealsLogged, spike]);

  // ── Shared level config ──────────────────────────────────────────────────
  // Prefer API spike_category over numeric thresholds so card color always
  // matches what the server classified (e.g. "High" even if spike = 40.5).
  const levelKey = (() => {
    const cat = glucoseSpikeData?.spike_category?.toLowerCase();
    if (cat === "low") return "low";
    if (cat === "medium" || cat === "moderate") return "moderate";
    if (cat === "high") return "high";
    // Fallback: numeric thresholds
    if (spike < 40) return "low";
    if (spike < 80) return "moderate";
    return "high";
  })();

  const level = (() => {
    if (levelKey === "low")
      return {
        label: "Low Impact",
        shortLabel: "LOW",
        headerGrad: "from-emerald-500 to-green-500",
        heroBg: "from-emerald-500 via-green-600 to-teal-600",
        heroShadow: "shadow-emerald-400/40",
        cardBg: "from-emerald-50/80 to-green-50/50 dark:from-emerald-950/30 dark:to-green-950/15",
        border: "border-emerald-200 dark:border-emerald-800/40",
        valueColor: "text-emerald-600 dark:text-emerald-400",
        arcColor: GLUCOSE_CHART.low,
        ringColor: GLUCOSE_CHART.low,
        glowFilter: `drop-shadow(0 0 5px ${GLUCOSE_CHART.low}77)`,
        tipBg: "bg-white/70 dark:bg-white/5 border-emerald-100 dark:border-emerald-800/30",
        waveColor: GLUCOSE_CHART.low,
        zoneLow: GLUCOSE_CHART.low,
        zoneMid: GLUCOSE_CHART.moderate,
        zoneHigh: GLUCOSE_CHART.high,
      };
    if (levelKey === "moderate")
      return {
        label: "Moderate Impact",
        shortLabel: "MODERATE",
        headerGrad: "from-orange-500 to-amber-400",
        heroBg: "from-orange-500 via-amber-400 to-yellow-500",
        heroShadow: "shadow-orange-400/40",
        cardBg: "from-orange-50/80 to-amber-50/50 dark:from-orange-950/30 dark:to-amber-950/15",
        border: "border-orange-200 dark:border-orange-800/40",
        valueColor: "text-orange-600 dark:text-orange-400",
        arcColor: GLUCOSE_CHART.moderate,
        ringColor: GLUCOSE_CHART.moderate,
        glowFilter: `drop-shadow(0 0 5px ${GLUCOSE_CHART.moderate}77)`,
        tipBg: "bg-white/70 dark:bg-white/5 border-orange-100 dark:border-orange-800/30",
        waveColor: GLUCOSE_CHART.moderate,
        zoneLow: GLUCOSE_CHART.low,
        zoneMid: GLUCOSE_CHART.moderate,
        zoneHigh: GLUCOSE_CHART.high,
      };
    return {
      label: "High Impact",
      shortLabel: "HIGH",
      headerGrad: "from-red-600 to-red-400",
      heroBg: "from-red-600 via-red-500 to-rose-500",
      heroShadow: "shadow-red-500/40",
      cardBg: "from-red-50/80 to-rose-50/50 dark:from-red-950/30 dark:to-rose-950/15",
      border: "border-red-200 dark:border-red-800/40",
      valueColor: "text-red-600 dark:text-red-400",
      arcColor: GLUCOSE_CHART.high,
      ringColor: GLUCOSE_CHART.high,
      glowFilter: `drop-shadow(0 0 7px ${GLUCOSE_CHART.high}88)`,
      tipBg: "bg-white/70 dark:bg-white/5 border-red-100 dark:border-red-800/30",
      waveColor: GLUCOSE_CHART.high,
      zoneLow: GLUCOSE_CHART.low,
      zoneMid: GLUCOSE_CHART.moderate,
      zoneHigh: GLUCOSE_CHART.high,
    };
  })();

  const headerBadgeLabel = glucoseSpikeData?.spike_category
    ? glucoseSpikeData.spike_category.toUpperCase()
    : level.shortLabel;

  // Always use frontend-defined guidance (do not override from API ui_message).
  const normalizedCategory = glucoseSpikeData?.spike_category?.toLowerCase();
  const tip =
    normalizedCategory === "low"
      ? "Excellent! This meal has minimal impact on blood sugar. A short walk after eating helps maintain stable levels."
      : normalizedCategory === "medium" || normalizedCategory === "moderate"
        ? "To lower this spike: Add more fiber (vegetables, salad) or protein (eggs, chicken, paneer). Choose whole grains instead of refined carbs. Drink plenty of water and take a 10-minute walk after eating."
        : normalizedCategory === "high"
          ? "High spike warning! To reduce it: Split this meal into 2 smaller portions, replace simple carbs with complex carbs (brown rice, quinoa), add lean protein and green vegetables. Consider eating this after exercise when your body uses glucose faster."
          : spike < 40
            ? "Excellent! This meal has minimal impact on blood sugar. A short walk after eating helps maintain stable levels."
            : spike < 80
              ? "To lower this spike: Add more fiber (vegetables, salad) or protein (eggs, chicken, paneer). Choose whole grains instead of refined carbs. Drink plenty of water and take a 10-minute walk after eating."
              : "High spike warning! To reduce it: Split this meal into 2 smaller portions, replace simple carbs with complex carbs (brown rice, quinoa), add lean protein and green vegetables. Consider eating this after exercise when your body uses glucose faster.";

  const rangeMessage = glucoseSpikeData?.spike_range
    ? `Your current blood glucose is predicted to range from ${Number(glucoseSpikeData.spike_range.low).toFixed(1)} to ${Number(glucoseSpikeData.spike_range.high).toFixed(1)} mg/dL because of this logged meal.`
    : null;

  // ── Shared Stats Row ─────────────────────────────────────────────────────
  const StatsRow = () => (
    <div className="mt-3 grid grid-cols-3 divide-x divide-border/50 rounded-xl border border-border/40 bg-white/55 dark:bg-white/5">
      <div className="flex flex-col items-center px-1.5 py-3">
        <TrendingUp className="mb-1 h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-base font-bold text-foreground">{timeToPeak}m</span>
        <span className="mt-0.5 text-center text-xs font-medium text-muted-foreground">Time to Peak</span>
      </div>
      <div className="flex flex-col items-center px-1.5 py-3">
        <Clock className="mb-1 h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-base font-bold text-foreground">{recoveryTime}m</span>
        <span className="mt-0.5 text-center text-xs font-medium text-muted-foreground">Recovery</span>
      </div>
      <div className="flex flex-col items-center px-1.5 py-3">
        <Activity className="mb-1 h-3.5 w-3.5 text-muted-foreground" />
        <span className={`text-base font-bold ${confidence.color}`}>{confidence.label}</span>
        <span className="mt-0.5 text-center text-xs font-medium text-muted-foreground">Confidence</span>
      </div>
    </div>
  );

  // ── Shared Tip Row ───────────────────────────────────────────────────────
  const TipRow = () => (
    <div className={`mt-2.5 rounded-xl border px-3 py-2.5 ${level.tipBg}`}>
      <p className="text-sm font-bold text-foreground mb-1">Glucose Management Tip</p>
      <p className="text-sm leading-relaxed text-muted-foreground">{tip}</p>
    </div>
  );

  const RangeInfoRow = () => {
    if (!rangeMessage) return null;

    return (
      <div className="mt-2.5 rounded-xl border border-border/40 bg-white/55 px-3 py-2.5 dark:bg-white/5">
        <p className="text-footnote leading-relaxed text-muted-foreground">{rangeMessage}</p>
      </div>
    );
  };

  const CumulativeNoteRow = () => {
    const note = glucoseSpikeData?.cumulative_note;
    if (!note) return null;
    return (
      <div className="mt-2.5 flex items-start gap-2 rounded-xl border border-amber-200/60 bg-amber-50/70 dark:bg-amber-900/20 dark:border-amber-800/30 px-3 py-2.5">
        <span className="mt-0.5 text-amber-500">
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </span>
        <p className="text-footnote leading-relaxed text-amber-700 dark:text-amber-400">{note}</p>
      </div>
    );
  };

  // ── Educational Info Section ─────────────────────────────────────────────
  const EducationSection = () => (
    <div className="mt-3">
      <div className="rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-800/30 px-3 py-2">
        <p className="text-footnote font-semibold text-blue-700 dark:text-blue-400 mb-1">About This Prediction</p>
        <p className="text-footnote leading-relaxed text-muted-foreground">
          Shows how much your blood sugar may rise after eating this meal. Lower spikes mean more stable energy levels.
        </p>
      </div>
    </div>
  );

  // ── Shared Header ────────────────────────────────────────────────────────
  const Header = () => (
    <div className={`bg-gradient-to-r ${level.headerGrad} flex items-center justify-between gap-2 px-4 py-2.5`}>
      <div className="flex min-w-0 items-center gap-2">
        <Zap className="h-4 w-4 shrink-0 text-white" />
        <span className="truncate text-sm font-bold uppercase tracking-wide text-white">
          {glucoseSpikeData?.spike_category ? `${glucoseSpikeData.spike_category} Glucose Spike` : "Predicted Glucose Spike"}
        </span>
      </div>
      <span className="shrink-0 rounded-full border border-white/30 bg-white/20 px-2.5 py-0.5 text-xs font-bold uppercase tracking-widest text-white">
        {headerBadgeLabel}
      </span>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VARIANT — "curve"  (Glucose Response Curve with time markers)
  // ════════════════════════════════════════════════════════════════════════
  if (variant === "curve") {
    // Generate glucose response curve data points - 5 points only
    const baseline = glucoseSpikeData?.baseline_glucose ?? 95;
    const prevMeal = glucoseSpikeData?.previous_meal_remaining_mg_dl ?? 0;
    const thisMealSpike = glucoseSpikeData?.this_meal_spike_mg_dl ?? spike;
    const peakValue = baseline + prevMeal + thisMealSpike;
    const timeToPeak = glucoseSpikeData?.time_to_peak_min ?? 60;
    const recoveryTime = glucoseSpikeData?.recovery_time_min ?? 90;
    const confidence = glucoseSpikeData?.confidence ?? "medium";
    const isCumulative = glucoseSpikeData?.is_cumulative ?? false;
    const cumulativeNote = glucoseSpikeData?.cumulative_note ?? null;
    const spikeRangeLow = glucoseSpikeData?.spike_range?.low ?? 0;
    const spikeRangeHigh = glucoseSpikeData?.spike_range?.high ?? 0;
    const spikeCategory = glucoseSpikeData?.spike_category ?? null;
    const spikeCatLc = spikeCategory?.toLowerCase() ?? "";

    // Dynamic colors — same severity hex palette as gauge/wave/hero (`GLUCOSE_CHART`)
    const categoryTheme =
      spikeCatLc === "low"
        ? {
            gradient: "from-emerald-500 to-green-500",
            text: "text-emerald-600 dark:text-emerald-400",
            dot: "bg-emerald-500",
            bg: "bg-gradient-to-br from-emerald-50/50 via-green-50/30 to-slate-50/50",
          }
        : spikeCatLc === "moderate" || spikeCatLc === "medium"
          ? {
              gradient: "from-orange-500 to-amber-500",
              text: "text-orange-600 dark:text-orange-400",
              dot: "bg-orange-500",
              bg: "bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-slate-50/50",
            }
          : spikeCatLc === "high"
            ? {
                gradient: "from-red-600 to-red-500",
                text: "text-red-600 dark:text-red-400",
                dot: "bg-red-500",
                bg: "bg-gradient-to-br from-red-50/50 via-rose-50/30 to-slate-50/50",
              }
            : {
                gradient: "from-slate-500 to-slate-400",
                text: "text-slate-600 dark:text-slate-400",
                dot: "bg-slate-500",
                bg: "bg-slate-50",
              };

    // Use real API glucose_curve.points if available, else fall back to calculated points
    const apiCurvePoints = glucoseSpikeData?.glucose_curve?.points;
    const curve: { time: number; value: number; label: string }[] = apiCurvePoints && apiCurvePoints.length > 0
      ? apiCurvePoints.map((p: GlucoseCurvePoint) => ({
          time: p.time_min,
          value: p.glucose_mg_dl,
          label: `${p.time_min}`,
        }))
      : [
          { time: 0, value: baseline + prevMeal, label: "0" },
          { time: 30, value: baseline + prevMeal + thisMealSpike * 0.35, label: "30" },
          { time: timeToPeak, value: peakValue, label: `${timeToPeak}` },
          { time: 90, value: baseline + prevMeal + thisMealSpike * 0.45, label: "90" },
          { time: 120, value: baseline + prevMeal + thisMealSpike * 0.2, label: "120" },
        ];
    // sort by time in case timeToPeak is unusual
    curve.sort((a, b) => a.time - b.time);
    // peak = highest glucose value in the curve
    const peakIndex = curve.reduce((maxIdx, p, i, arr) => p.value > arr[maxIdx].value ? i : maxIdx, 0);

    // SVG chart dimensions
    const chartW = 320;
    const chartH = 200;
    const padding = { top: 24, right: 16, bottom: 38, left: 32 };
    const plotW = chartW - padding.left - padding.right;
    const plotH = chartH - padding.top - padding.bottom;

    // Fixed X-axis: always 0–120 min
    const xMax = 120;
    const maxGlucoseInCurve = curve.length > 0 ? Math.max(...curve.map(p => p.value)) : 160;
    const maxVal = Math.max(Math.ceil(maxGlucoseInCurve / 10) * 10 + 10, 170);
    const minVal = 75;
    const xScale = (t: number) => padding.left + (t / xMax) * plotW;
    const yScale = (v: number) => padding.top + (1 - (v - minVal) / (maxVal - minVal)) * plotH;

    // Generate smooth path
    const points = curve.map(p => ({ x: xScale(p.time), y: yScale(p.value) }));
    const pathD = points.map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = points[i - 1];
      const cpX = (prev.x + p.x) / 2;
      return `C ${cpX} ${prev.y}, ${cpX} ${p.y}, ${p.x} ${p.y}`;
    }).join(" ");

    // Fill path for gradient area
    const fillD = `${pathD} L ${xScale(xMax)} ${yScale(minVal)} L ${padding.left} ${yScale(minVal)} Z`;

    const curveColor = (() => {
      if (spikeCatLc === "low") return GLUCOSE_CHART.low;
      if (spikeCatLc === "moderate" || spikeCatLc === "medium") return GLUCOSE_CHART.moderate;
      if (spikeCatLc === "high") return GLUCOSE_CHART.high;
      return "#64748b";
    })();

    // Peak label + value at 30 min (food scan standard window)
    const PEAK_LABEL_MIN = 30;
    const peakAt30Min = apiCurvePoints?.find((p) => p.time_min === PEAK_LABEL_MIN);
    const peakPhasePoint = apiCurvePoints?.find((p) => p.phase === "peak");
    const peakGlucose =
      peakAt30Min?.glucose_mg_dl ??
      peakPhasePoint?.glucose_mg_dl ??
      glucoseSpikeData?.glucose_curve?.peak_glucose ??
      curve[peakIndex]?.value ??
      glucoseSpikeData?.predicted_peak_glucose ??
      glucoseSpikeData?.predicted_peak_glucose_this_meal_only ??
      0;

    // Start glucose — driven strictly by is_cumulative at the top level of the spike data:
    //   is_cumulative === true  → use glucose_curve.starting_glucose
    //                             (already includes the previous meal's remaining glucose)
    //   is_cumulative === false → use baseline_glucose
    //                             (pure resting / fasting baseline)
    const isCumulativeVal = glucoseSpikeData?.is_cumulative === true;
    const startGlucose = isCumulativeVal
      ? (glucoseSpikeData?.glucose_curve?.starting_glucose ?? baseline)
      : (glucoseSpikeData?.baseline_glucose ?? baseline);

    return (
      <div className="overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-lg border border-slate-100 dark:border-slate-800">

        {/* ── Top header strip ── */}
        <div className={`bg-gradient-to-r ${categoryTheme.gradient} px-4 py-1.5 flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="h-4 w-4 text-white flex-shrink-0" />
            <span className="text-white text-sm font-bold tracking-wide truncate">Glucose Response</span>
          </div>
          <span className="rounded-full bg-white/25 border border-white/40 px-3 py-0.5 text-xs font-extrabold text-white tracking-wide uppercase whitespace-nowrap flex-shrink-0">
            {spikeCategory || "—"}
          </span>
        </div>

        {/* ── Key numbers row ── */}
        <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-800 border-b border-slate-100 dark:border-slate-800">
          <div className="flex flex-col items-center py-1.5 px-2">
            <span className="text-compact-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Start</span>
            <span className="text-xl font-black text-slate-700 dark:text-slate-200">{startGlucose.toFixed(1)}</span>
            <span className="text-compact-xs text-slate-400">mg/dL</span>
          </div>
          <div className="flex flex-col items-center py-1.5 px-2">
            <span className="mb-1 text-center text-compact-xs font-semibold leading-tight text-slate-400">
              30 min peak
            </span>
            <span className={`text-3xl font-black ${categoryTheme.text}`}>{Math.round(peakGlucose)}</span>
            <span className="text-compact-xs text-slate-400">mg/dL</span>
          </div>
          <div className="flex flex-col items-center py-1.5 px-2">
            <span className="text-compact-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Spike</span>
            <span className={`text-xl font-black ${categoryTheme.text}`}>+{thisMealSpike.toFixed(1)}</span>
            <span className="text-compact-xs text-slate-400">mg/dL</span>
          </div>
        </div>

        {/* ── Chart ── */}
        <div className="px-3 pt-3 pb-1">
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Predicted Glucose Curve</h3>
          <svg width="100%" viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="xMidYMid meet" className="overflow-visible">
            <defs>
              <linearGradient id="gcFill" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={curveColor} stopOpacity="0.25" />
                <stop offset="100%" stopColor={curveColor} stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {/* Horizontal grid lines */}
            {[80, 100, 120, 140, 160].map((val) => (
              <line key={val} x1={padding.left} y1={yScale(val)} x2={padding.left + plotW} y2={yScale(val)}
                stroke="#f1f5f9" strokeWidth="1" />
            ))}

            {/* Y-axis labels */}
            {[80, 100, 120, 140, 160].map((val) => (
              <text key={val} x={padding.left - 5} y={yScale(val)} fontSize="var(--font-size-2xs)" fill="#475569"
                textAnchor="end" dominantBaseline="middle" fontWeight="700">{val}</text>
            ))}

            {/* 140 spike threshold — subtle red dashed */}
            <line x1={padding.left} y1={yScale(140)} x2={padding.left + plotW} y2={yScale(140)}
              stroke="#fca5a5" strokeWidth="1.5" strokeDasharray="6 4" />

            {/* Axes */}
            <line x1={padding.left} y1={padding.top + plotH} x2={padding.left + plotW} y2={padding.top + plotH}
              stroke="#cbd5e1" strokeWidth="1.5" />
            <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + plotH}
              stroke="#cbd5e1" strokeWidth="1.5" />

            {/* Filled area */}
            <path d={fillD} fill="url(#gcFill)" />

            {/* Curve line */}
            <path d={pathD} fill="none" stroke={curveColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

            {/* Data points */}
            {curve.map((p, i) => {
              const isPeak = i === peakIndex;
              const cx = xScale(p.time);
              const cy = yScale(p.value);
              return (
                <g key={i}>
                  {isPeak && <circle cx={cx} cy={cy} r="9" fill={curveColor} opacity="0.18" />}
                  <circle cx={cx} cy={cy} r={isPeak ? 5.5 : 4}
                    fill={isPeak ? curveColor : "white"}
                    stroke={curveColor}
                    strokeWidth="2"
                  />
                </g>
              );
            })}

            {/* X-axis time labels */}
            {[0, 30, 60, 90, 120].map((t) => (
              <text key={t} x={xScale(t)} y={padding.top + plotH + 14} fontSize="var(--font-size-2xs)" fill="#475569"
                textAnchor="middle" fontWeight="700">{t}</text>
            ))}

            {/* X-axis title */}
            <text x={padding.left + plotW / 2} y={padding.top + plotH + 28} fontSize="var(--font-size-3xs)" fill="#94a3b8"
              textAnchor="middle">Time (min)</text>
          </svg>
        </div>

        {/* ── Bottom stats row ── */}
        <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-800 border-t border-slate-100 dark:border-slate-800">
          <div className="flex flex-col items-center py-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-slate-400 mb-1" />
            <span className="text-base font-black text-slate-700 dark:text-slate-200">{glucoseSpikeData?.time_to_peak_min ?? timeToPeak}m</span>
            <span className="text-compact-xs text-slate-400 font-medium">Time to Peak</span>
          </div>
          <div className="flex flex-col items-center py-1.5">
            <Clock className="h-3.5 w-3.5 text-slate-400 mb-1" />
            <span className="text-base font-black text-slate-700 dark:text-slate-200">{glucoseSpikeData?.recovery_time_min ?? recoveryTime}m</span>
            <span className="text-compact-xs text-slate-400 font-medium">Recovery</span>
          </div>
          <div className="flex flex-col items-center py-1.5">
            <Activity className="h-3.5 w-3.5 text-slate-400 mb-1" />
            <span className={`text-base font-black capitalize ${
              confidence === "high" ? "text-green-500"
              : confidence === "medium" ? "text-orange-400"
              : "text-red-400"
            }`}>{glucoseSpikeData?.confidence ?? confidence}</span>
            <span className="text-compact-xs text-slate-400 font-medium">Confidence</span>
          </div>
        </div>

        {/* ── Range note ── */}
        <div className="mx-3 mb-1.5 space-y-1">

          {/* Predicted range message */}
          {showPredictedRange && spikeRangeLow > 0 && spikeRangeHigh > 0 && (
            <div className="rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-800/30 px-3 py-1.5 flex items-start gap-2.5">
              <span className="flex-shrink-0 mt-0.5">
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </span>
              <p className="text-caption2 leading-relaxed text-slate-600 dark:text-slate-300">
                Your current blood glucose is predicted to range from{" "}
                <span className="font-extrabold text-blue-600 dark:text-blue-400">{spikeRangeLow}</span>
                {" "}to{" "}
                <span className="font-extrabold text-blue-600 dark:text-blue-400">{spikeRangeHigh} mg/dL</span>
                {" "}because of this logged meal.
              </p>
            </div>
          )}

          {/* Previous meal remaining — shown only when cumulative */}
          {isCumulative && prevMeal > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 px-3 py-1.5">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-400 flex-shrink-0" />
                <span className="text-compact-sm font-semibold text-amber-700 dark:text-amber-400">Prev. Meal Remaining</span>
              </div>
              <span className="text-caption2 font-extrabold text-amber-700 dark:text-amber-400">
                +{prevMeal.toFixed(1)} <span className="text-compact-xs font-semibold">mg/dL</span>
              </span>
            </div>
          )}

          {/* Cumulative note */}
          {cumulativeNote && (
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 px-3 py-1.5">
              <span className="mt-0.5 text-amber-500 flex-shrink-0">
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </span>
              <p className="text-compact-sm leading-snug text-amber-700 dark:text-amber-400">{cumulativeNote}</p>
            </div>
          )}

        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // VARIANT A — "gauge"  (Speedometer arc)
  // ════════════════════════════════════════════════════════════════════════
  if (variant === "gauge") {
    // Gauge geometry
    const needleAngle = Math.PI + animPct * Math.PI;
    const dotX = CX + R * Math.cos(needleAngle);
    const dotY = CY + R * Math.sin(needleAngle);
    const tick25Angle = Math.PI + 0.25 * Math.PI;
    const tick50Angle = Math.PI + 0.5 * Math.PI;
    const t25x = CX + R * Math.cos(tick25Angle);
    const t25y = CY + R * Math.sin(tick25Angle);
    const t50x = CX + R * Math.cos(tick50Angle);
    const t50y = CY + R * Math.sin(tick50Angle);
    const arcPath = `M 13 66 A 52 52 0 0 0 117 66`;

    return (
      <div
        className={`overflow-hidden rounded-2xl border ${level.border} bg-gradient-to-br ${level.cardBg} shadow-lg animate-fade-slide-up`}
      >
        <Header />
        <div className="px-4 pb-4 pt-3">
          <div className="flex items-center gap-3">
            {/* Speedometer SVG */}
            <div className="shrink-0">
              <svg width="130" height="72" viewBox="0 0 130 72" aria-hidden="true">
                <path d={`M 13 66 A 52 52 0 0 0 ${t25x} ${t25y}`} fill="none" stroke={level.zoneLow}  strokeWidth="9" strokeLinecap="butt" opacity="0.22" />
                <path d={`M ${t25x} ${t25y} A 52 52 0 0 0 ${t50x} ${t50y}`} fill="none" stroke={level.zoneMid}  strokeWidth="9" strokeLinecap="butt" opacity="0.22" />
                <path d={`M ${t50x} ${t50y} A 52 52 0 0 0 117 66`} fill="none" stroke={level.zoneHigh} strokeWidth="9" strokeLinecap="butt" opacity="0.22" />
                <path d={arcPath} fill="none" stroke={level.arcColor} strokeWidth="9" strokeLinecap="round"
                  strokeDasharray={ARC_LEN} strokeDashoffset={ARC_LEN * (1 - animPct)}
                  className="spike-arc-transition" style={{ filter: level.glowFilter }} />
                <circle cx={t25x} cy={t25y} r="2.5" fill="white" opacity="0.55" />
                <circle cx={t50x} cy={t50y} r="2.5" fill="white" opacity="0.55" />
                <circle cx={dotX} cy={dotY} r="7" fill={level.arcColor} opacity="0.25" className="spike-needle-transition" />
                <circle cx={dotX} cy={dotY} r="5" fill={level.arcColor} className="spike-needle-transition" style={{ filter: level.glowFilter }} />
                <circle cx={dotX} cy={dotY} r="2.5" fill="white" className="spike-needle-transition" />
              </svg>
            </div>
            {/* Value */}
            <div className="min-w-0 flex-1 pl-1">
              <p className={`text-display-sm font-extrabold leading-none tracking-tight animate-fade-slide-up-delay ${level.valueColor}`}>
                +{spike}
              </p>
              <p className="mt-1 text-base font-bold text-muted-foreground">mg/dL</p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: level.arcColor }} />
                <p className="text-sm font-bold text-muted-foreground">
                  {glucoseSpikeData ? `Prev: ${Number(glucoseSpikeData.previous_meal_remaining_mg_dl).toFixed(1)} mg/dL` : level.label}
                </p>
              </div>
            </div>
          </div>
          <RangeInfoRow />
          <CumulativeNoteRow />
          <StatsRow />
          <TipRow />
          <EducationSection />
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // VARIANT B — "wave"  (Blood-glucose spike curve chart)
  // ════════════════════════════════════════════════════════════════════════
  if (variant === "wave") {
    // SVG wave: flat baseline → rises to spike peak → gradual return
    // viewBox 0 0 200 64 — spike peak height scales with animPct
    const W = 200;
    const H = 64;
    const peakX = 90;
    const baseY = H - 8;
    const peakY = baseY - animPct * (H - 16);

    // Smooth bezier curve: flat → rise → spike → recovery
    const wavePath = `M 0 ${baseY} C 30 ${baseY}, 55 ${baseY}, 70 ${baseY} C 80 ${baseY}, 82 ${peakY + 6}, ${peakX} ${peakY} C ${peakX + 8} ${peakY - 2}, ${peakX + 18} ${peakY + 10}, ${peakX + 30} ${baseY - 4} C ${peakX + 50} ${baseY + 2}, ${W - 20} ${baseY}, ${W} ${baseY}`;
    const fillPath = `${wavePath} L ${W} ${H} L 0 ${H} Z`;

    return (
      <div
        className={`overflow-hidden rounded-2xl border ${level.border} bg-gradient-to-br ${level.cardBg} shadow-lg animate-fade-slide-up`}
      >
        <Header />
        <div className="px-4 pb-4 pt-3">
          {/* Value + label row */}
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-1">
                <span className={`text-4xl font-extrabold leading-none ${level.valueColor}`}>
                  +{spike}
                </span>
                <span className="text-base font-bold text-muted-foreground">mg/dL</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <Droplets className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-bold text-muted-foreground">
                  {glucoseSpikeData ? `Prev: ${Number(glucoseSpikeData.previous_meal_remaining_mg_dl).toFixed(1)} mg/dL` : level.label}
                </span>
              </div>
            </div>
            {/* Normal range info */}
            <div className="text-right">
              <p className="text-xs font-bold text-muted-foreground">Normal range</p>
              <p className="text-sm font-bold text-foreground">&lt;40 mg/dL</p>
            </div>
          </div>

          {/* Glucose spike chart */}
          <div className="relative overflow-hidden rounded-xl border border-border/30 bg-white/40 dark:bg-white/5">
            {/* Zone labels */}
            <div className="absolute left-1 top-1 flex flex-col gap-0.5">
              <span className="text-2xs font-semibold text-orange-400 opacity-70">HIGH</span>
              <span className="text-2xs font-semibold text-amber-400 opacity-70">MOD</span>
              <span className="text-2xs font-semibold text-emerald-500 opacity-70">LOW</span>
            </div>
            {/* Dashed reference lines */}
            <svg width="100%" height="72" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
              {/* Zone bands */}
              <rect x="0" y="0"  width={W} height={H * 0.35} fill="#f97316" opacity="0.06" />
              <rect x="0" y={H * 0.35} width={W} height={H * 0.3}  fill="#f59e0b" opacity="0.06" />
              <rect x="0" y={H * 0.65} width={W} height={H * 0.35} fill="#10b981" opacity="0.06" />
              {/* Dashed threshold lines */}
              <line x1="0" y1={H * 0.35} x2={W} y2={H * 0.35} stroke="#f97316" strokeWidth="0.8" strokeDasharray="4 3" opacity="0.4" />
              <line x1="0" y1={H * 0.65} x2={W} y2={H * 0.65} stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="4 3" opacity="0.4" />
              {/* Gradient fill under curve */}
              <defs>
                <linearGradient id="waveGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={level.waveColor} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={level.waveColor} stopOpacity="0.03" />
                </linearGradient>
              </defs>
              <path d={fillPath} fill="url(#waveGrad)" className="spike-wave-transition" />
              {/* Spike line */}
              <path d={wavePath} fill="none" stroke={level.waveColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className="spike-wave-transition" style={{ filter: level.glowFilter }} />
              {/* Peak dot */}
              <circle cx={peakX} cy={peakY} r="4.5" fill={level.waveColor}
                className="spike-wave-peak-transition" style={{ filter: level.glowFilter }} />
              <circle cx={peakX} cy={peakY} r="2" fill="white" className="spike-wave-peak-transition" />
            </svg>
            {/* X-axis labels */}
            <div className="flex justify-between px-3 pb-1.5">
              <span className="text-2xs text-muted-foreground">0 min</span>
              <span className="text-2xs text-muted-foreground">{timeToPeak} min peak</span>
              <span className="text-2xs text-muted-foreground">{recoveryTime} min</span>
            </div>
          </div>

          <RangeInfoRow />
          <CumulativeNoteRow />
          <StatsRow />
          <TipRow />
          <EducationSection />
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // VARIANT C — "ring"  (Circular donut ring + big number)
  // ════════════════════════════════════════════════════════════════════════
  // Donut ring geometry
  const RR = 44;       // ring radius
  const CIRC = 2 * Math.PI * RR;
  const dashOffset = CIRC * (1 - animPct);

  if (variant === "ring") {
    return (
    <div
      className={`overflow-hidden rounded-2xl border ${level.border} bg-gradient-to-br ${level.cardBg} shadow-lg animate-fade-slide-up`}
    >
      <Header />
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-center gap-4">
          {/* Donut ring */}
          <div className="relative shrink-0">
            <svg width="104" height="104" viewBox="0 0 104 104" aria-hidden="true">
              {/* Track */}
              <circle cx="52" cy="52" r={RR} fill="none" stroke="currentColor" strokeWidth="10"
                className="text-border/30" />
              {/* Zone arc — bg */}
              <circle cx="52" cy="52" r={RR} fill="none" stroke={level.arcColor} strokeWidth="10"
                strokeOpacity="0.15" />
              {/* Animated fill arc */}
              <circle cx="52" cy="52" r={RR} fill="none" stroke={level.arcColor} strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={CIRC}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 52 52)"
                className="spike-ring-transition"
                style={{ filter: level.glowFilter }}
              />
              {/* Glow dot at leading edge */}
              {(() => {
                const angle = -Math.PI / 2 + animPct * 2 * Math.PI;
                const dx = 52 + RR * Math.cos(angle);
                const dy = 52 + RR * Math.sin(angle);
                return (
                  <>
                    <circle cx={dx} cy={dy} r="7" fill={level.arcColor} opacity="0.2"
                      className="spike-ring-dot-transition" />
                    <circle cx={dx} cy={dy} r="5" fill={level.arcColor}
                      className="spike-ring-dot-transition" style={{ filter: level.glowFilter }} />
                    <circle cx={dx} cy={dy} r="2" fill="white"
                      className="spike-ring-dot-transition" />
                  </>
                );
              })()}
            </svg>
            {/* Centre text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-xl font-extrabold leading-none ${level.valueColor}`}>+{spike}</span>
              <span className="text-xs font-bold text-muted-foreground">mg/dL</span>
            </div>
          </div>

          {/* Right: label + mini bar scale */}
          <div className="flex-1 space-y-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Glucose Spike</p>
              <p className={`text-xl font-black ${level.valueColor}`}>
                {glucoseSpikeData ? `Prev: ${Number(glucoseSpikeData.previous_meal_remaining_mg_dl).toFixed(1)} mg/dL` : level.label}
              </p>
            </div>
            {/* Scale bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-2xs font-semibold text-muted-foreground">
                <span>0</span><span>40</span><span>80</span><span>160</span>
              </div>
              <div className="relative h-2.5 overflow-hidden rounded-full">
                {/* Rainbow track */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-400 via-amber-400 to-orange-500 opacity-25" />
                {/* Fill */}
                <div className="spike-bar-transition h-full rounded-full bg-gradient-to-r from-emerald-400 via-amber-400 to-orange-500"
                  style={{ width: `${animPct * 100}%`, filter: level.glowFilter }} />
              </div>
              {/* Pointer */}
              <div className="spike-pointer-transition relative h-1" style={{ marginLeft: `calc(${animPct * 100}% - 4px)` }}>
                <div className="mx-auto h-1.5 w-1.5 rounded-full" style={{ backgroundColor: level.arcColor }} />
              </div>
            </div>
            {/* Stats inline */}
            <div className="flex gap-3 text-xs">
              <div>
                <span className="font-bold text-foreground">{timeToPeak}m</span>
                <span className="ml-0.5 text-muted-foreground">peak</span>
              </div>
              <div>
                <span className="font-bold text-foreground">{recoveryTime}m</span>
                <span className="ml-0.5 text-muted-foreground">recovery</span>
              </div>
            </div>
          </div>
        </div>

        {/* Full stats + tip */}
        <RangeInfoRow />
        <CumulativeNoteRow />
        <StatsRow />
        <TipRow />
        <EducationSection />
      </div>
    </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // VARIANT D — "hero"  (Rich gradient hero, instant wow-factor ★)
  // ════════════════════════════════════════════════════════════════════════
  const spikeInt = Math.floor(spike);
  const spikeDecimal = spike.toFixed(1).split(".")[1];

  return (
    <div
      className={`overflow-hidden rounded-2xl shadow-2xl ${level.heroShadow} animate-fade-slide-up`}
    >
      {/* ── Rich gradient hero section ── */}
      <div
        className={`relative overflow-hidden bg-gradient-to-br ${level.heroBg} px-5 pb-5 pt-4`}
      >
        {/* Background radial glow blob */}
        <div className="spike-glow-blob-tr pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full opacity-20" />
        <div className="spike-glow-blob-bl pointer-events-none absolute -bottom-6 -left-6 h-32 w-32 rounded-full opacity-10" />

        {/* Top pill row */}
        <div className="relative mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm">
              <Zap className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="text-compact-sm font-semibold uppercase tracking-widest text-white/85">
              Glucose Spike
            </span>
          </div>
          <span className="rounded-full border border-white/30 bg-white/20 px-3 py-0.5 text-compact-xs font-bold uppercase tracking-[0.12em] text-white backdrop-blur-sm">
            {level.shortLabel}
          </span>
        </div>

        {/* ── Giant number ── */}
        <div className="relative mb-4 flex items-start justify-center gap-0.5">
          {/* Plus sign */}
          <span className="spike-hero-plus mt-2 text-3xl font-black text-white/80">
            +
          </span>
          {/* Integer part */}
          <span className="spike-hero-number text-7xl font-black leading-none tracking-tight text-white">
            {spikeInt}
          </span>
          {/* Decimal + unit stacked */}
          <div className="mb-1 ml-0.5 mt-auto flex flex-col items-start pb-1">
            <span className="text-2xl font-black leading-none text-white/75">.{spikeDecimal}</span>
            <span className="mt-0.5 text-compact-sm font-bold uppercase tracking-wider text-white/60">
              mg/dL
            </span>
          </div>
        </div>

        {/* Sub label */}
        <p className="mb-4 text-center text-sm font-semibold text-white/75">{level.label}</p>

        {/* ── Segmented spike track ── */}
        <div className="relative">
          {/* Track background with 3 zones */}
          <div className="relative h-3.5 w-full overflow-hidden rounded-full">
            {/* Zone 1 green 0-25% */}
            <div className="absolute left-0 top-0 h-full w-[25%] bg-emerald-300 opacity-35 rounded-l-full" />
            {/* Zone 2 amber 25-50% */}
            <div className="absolute left-[25%] top-0 h-full w-[25%] bg-amber-300 opacity-35" />
            {/* Zone 3 orange 50-100% */}
            <div className="absolute left-[50%] top-0 h-full w-[50%] bg-orange-300 opacity-35 rounded-r-full" />
            {/* Animated fill */}
            <div
              className="spike-hero-fill absolute left-0 top-0 h-full rounded-full bg-white"
              style={{ width: `${animPct * 100}%` }}
            />
            {/* Glowing needle at fill edge */}
            <div
              className="spike-hero-needle absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
              style={{ left: `${animPct * 100}%` }}
            />
          </div>
          {/* Zone labels */}
          <div className="mt-1.5 grid grid-cols-4 text-2xs font-semibold text-white/50">
            <span className="text-center">0</span>
            <span className="text-center">40</span>
            <span className="text-center">80</span>
            <span className="text-right pr-1">160</span>
          </div>
        </div>
      </div>

      {/* ── Stats + tip on white/card bg ── */}
      <div className="bg-card px-4 pb-3 pt-2">
        <StatsRow />
        <TipRow />
        <EducationSection />
      </div>
    </div>
  );
};

export default PredictedGlucoseSpikeCard;
